export interface WeaponStats {
  damage: number;
  reloadMs: number;
  projectileSpeed: number;
  range: number;
  projectileLifetimeMs: number;
  streams: number;
}

export interface WeaponUpgrade {
  damage: 0 | 1 | 2 | 3;
  reload: 0 | 1 | 2 | 3;
  velocity: 0 | 1 | 2 | 3;
  range: 0 | 1 | 2 | 3;
  streams?: number;
}

export const DEFAULT_WEAPON_UPGRADES: WeaponUpgrade = {
  damage: 0,
  reload: 0,
  velocity: 0,
  range: 0,
  streams: 0,
};

/** Clamp a weapon upgrade to the levels the player actually owns. */
export function clampWeaponUpgrades(
  upgrades: WeaponUpgrade,
  ownedLevels: Partial<Record<keyof WeaponUpgrade, number>>,
): WeaponUpgrade {
  const cap = (val: 0|1|2|3, key: keyof WeaponUpgrade): 0|1|2|3 =>
    Math.min(val, ownedLevels[key] ?? 0) as 0|1|2|3;
  return {
    damage:   cap(upgrades.damage,   "damage"),
    reload:   cap(upgrades.reload,   "reload"),
    velocity: cap(upgrades.velocity, "velocity"),
    range:    cap(upgrades.range,    "range"),
    streams:  cap((upgrades.streams ?? 0) as 0|1|2|3, "streams"),
  };
}

/**
 * Clamp an untrusted weapon upgrade object received over the network.
 * Each field is floored and clamped to [0, 3].
 */
export function sanitizeWeaponUpgrades(raw: unknown): WeaponUpgrade {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_WEAPON_UPGRADES };
  const r = raw as Record<string, unknown>;
  const clamp = (v: unknown): 0 | 1 | 2 | 3 => {
    const n = typeof v === "number" ? v : 0;
    return Math.max(0, Math.min(3, Math.floor(n))) as 0 | 1 | 2 | 3;
  };
  return {
    damage:   clamp(r["damage"]),
    reload:   clamp(r["reload"]),
    velocity: clamp(r["velocity"]),
    range:    clamp(r["range"]),
    streams:  clamp(r["streams"]),
  };
}
